import type {
  IFilterMap,
  IFiltersList,
  IFiltersMap,
  IPhoneFilter,
  ISearchFilter,
  ISelectFilter,
  ITextFilter,
  TFieldValidation,
  TFilters,
  TFiltersParams,
  TTextValuePair,
} from '../../types/data_table';

/**
 * Helpers that translate the API-shaped `filtersMap` a consumer passes to
 * `DataTable` into the `filtersList` the `FiltersBar` actually renders, and
 * that keep that list in sync as the language changes.
 *
 * `filtersMap` is the wire format (snake-ish keys, options as single-entry
 * objects); `filtersList` is the component format (`{ id, type, label, ... }`).
 * Keeping the translation here means the container never deals with the wire
 * format and the bar never deals with anything else.
 */

/** Label used when a select filter has no matching `defaultSelection`. */
const ALL_OPTION_FALLBACK = 'All';

/** A filter rendered as an input rather than a dropdown. */
type TFieldFilter = ITextFilter | IPhoneFilter | ISearchFilter;

/** `filtersMap` types that become a dropdown in the bar. */
const SELECT_MAP_TYPES: IFilterMap['type'][] = ['select', 'multi_selection'];

/** `filtersMap` types that become a text/phone/search input in the bar. */
const FIELD_MAP_TYPES: IFilterMap['type'][] = ['text', 'phone', 'search'];

/**
 * Options arrive as single-entry objects keyed by the label the user sees —
 * `{ 'Successful': 'success' }` — so flatten them into the `{ text, value }`
 * pairs every dropdown component in the design system expects.
 */
const toOptions = (values: IFilterMap['values']): TTextValuePair[] =>
  (Array.isArray(values) ? values : []).map((item) => {
    const [text] = Object.keys(item);
    return { text, value: item[text] };
  });

/**
 * Resolves the label shown before the user picks anything. `defaultSelection`
 * holds a raw option *value*, so it has to be looked up among the options to
 * find the label that goes with it.
 */
const toDefaultOptionText = (
  values: IFilterMap['values'],
  defaultSelection: IFilterMap['defaultSelection'],
  allLabel?: string
): string => {
  const match = (Array.isArray(values) ? values : []).find(
    (item) => Object.values(item)[0] === defaultSelection
  );

  return match ? Object.keys(match)[0] : allLabel || ALL_OPTION_FALLBACK;
};

/** Renames the wire format's `{ regex, message }` to the bar's own shape. */
const toValidations = (
  validations: IFilterMap['validations']
): TFieldValidation[] =>
  (validations || []).map(({ regex, message }) => ({
    pattern: regex,
    error_msg: message,
  }));

/**
 * Builds a dropdown filter. Multi-selection filters start out with every
 * option selected, which the bar reads as "no narrowing applied" and renders
 * as the `all` label.
 */
const buildSelectFilter = (
  filter: IFilterMap,
  allLabel?: string
): ISelectFilter => {
  const {
    key,
    name,
    type,
    values,
    defaultSelection,
    countList,
    multipleSelectionSummaryType,
    multiSelectionParamType,
  } = filter;

  const options = toOptions(values);

  return {
    id: key,
    type: 'select',
    label: name,
    options,
    defaultOption: toDefaultOptionText(values, defaultSelection, allLabel),
    countList,
    multipleSelectionSummaryType:
      multipleSelectionSummaryType || 'selection_count',
    ...(type === 'multi_selection'
      ? {
          selectMultiple: true,
          required: true,
          selectedOptions: options,
          multiSelectionParamType,
        }
      : {}),
  };
};

/**
 * Builds a text, phone or search filter. `values` carries the initial input
 * value for these types rather than a list of options.
 */
const buildFieldFilter = (filter: IFilterMap): TFieldFilter => {
  const { key, name, type, values, placeHolder, validations } = filter;

  return {
    id: key,
    type: type as TFieldFilter['type'],
    label: name,
    placeholder: placeHolder,
    validations: toValidations(validations),
    value: values as string,
  };
};

/**
 * Converts a `filtersMap` into the list the `FiltersBar` renders.
 *
 * Entries with an unrecognised `type` are dropped. `filtersMap` is usually
 * built from an API response, so a type the frontend does not handle yet is a
 * live possibility — the backend adding a filter ahead of the UI. Dropping
 * those keeps the rest of the bar working; leaving them in used to throw while
 * grouping the filter types and take the whole table down.
 *
 * @param filtersMap Wire-format filters, or `undefined` when the table has none.
 * @param allLabel   Localized "All" label, used as the select fallback.
 */
export const buildFiltersList = (
  filtersMap: IFiltersMap | undefined,
  allLabel?: string
): IFiltersList =>
  (filtersMap || []).reduce<IFiltersList>((list, filter) => {
    if (SELECT_MAP_TYPES.includes(filter.type)) {
      list.push(buildSelectFilter(filter, allLabel));
    } else if (FIELD_MAP_TYPES.includes(filter.type)) {
      list.push(buildFieldFilter(filter));
    }

    return list;
  }, []);

/**
 * The params a freshly built list already implies.
 *
 * A text or phone filter can arrive with a value on it — a filter restored
 * from the URL, say — and that value has to be in `params` before the first
 * request goes out, or the table fetches once without it and again with it.
 */
export const seedParamsFromFilters = (
  filtersList: IFiltersList
): TFiltersParams =>
  filtersList.reduce<TFiltersParams>((params, filter) => {
    if (filter.type === 'text' || filter.type === 'phone') {
      if (typeof filter.value === 'string' && filter.value) {
        params[filter.id] = filter.value;
      }
    }

    return params;
  }, {});

/** Reads the selected option values off a filter, single or multi selection. */
const toSelectedValues = (
  selectedOptions: ISelectFilter['selectedOptions']
): string[] => {
  if (Array.isArray(selectedOptions)) {
    return selectedOptions.map((option) => option.value);
  }

  return selectedOptions ? [selectedOptions.value] : [];
};

/**
 * Re-applies the user's current selections on top of a freshly built list.
 *
 * Rebuilding is how the bar picks up new labels, options and placeholders when
 * the language changes; matching on option *value* (never on label, which is
 * exactly what just changed) keeps the user's choices intact across the swap.
 *
 * @param defaults Freshly built list, carrying the new language's copy.
 * @param previous The list currently on screen, carrying the user's selections.
 */
export const preserveSelections = (
  defaults: IFiltersList,
  previous: IFiltersList
): IFiltersList => {
  if (!previous.length) return defaults;

  return defaults.map((filter) => {
    const previousFilter = previous.find((item) => item.id === filter.id);
    if (!previousFilter) return filter;

    if (filter.type !== 'select') {
      return {
        ...filter,
        value: (previousFilter as TFieldFilter).value ?? filter.value,
      };
    }

    const options = filter.options || [];
    const selectedValues = toSelectedValues(
      (previousFilter as ISelectFilter).selectedOptions
    );

    return {
      ...filter,
      selectedOptions: filter.selectMultiple
        ? options.filter((option) => selectedValues.includes(option.value))
        : options.find((option) => option.value === selectedValues[0]),
    };
  });
};

/**
 * Groups filter ids by their type — `{ select: ['status'], search: ['q'] }`.
 *
 * Handed to the consumer's `onResponseChange` so it can tell which of the
 * applied params came from which kind of control.
 */
export const groupFilterIdsByType = (
  filtersList: IFiltersList
): Record<TFilters, string[]> =>
  filtersList.reduce((groups, filter) => {
    groups[filter.type] = [...(groups[filter.type] || []), filter.id];
    return groups;
  }, {} as Record<TFilters, string[]>);
